import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Input,
  signal,
  ViewChild,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { ModalService } from '@src/shared/modal/modal.service';
import { TableRow } from '../../../../../src/shared/presentational/woo-table/woo-table.type';
import { ExtraProductFormatField, ProductFormatService } from '../../../../woo_services.module/ProductFormatService';
import { ExtraPublisherField, PublisherService } from '../../../../woo_services.module/PublisherService';
import { ProductFormat, Publisher, SlotType } from '../../../../woo_services.module/shared-types';
import { TranslationService } from '../../../../woo_services.module/TranslationService';

export type SlotTypeEstimates = Partial<Record<SlotType, number>>;
export type SavePayload = Record<string, number>;

@Component({
  selector: 'manage-ag-withdrawal-limits',
  templateUrl: './manage-ag-withdrawal-limits.component.html',
  styleUrls: ['../../shared/table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManageAgWithdrawalLimits {
  @ViewChild('stickyTableHeader', { static: true }) stickyTableHeader!: ElementRef;
  @ViewChild('agWithdrawalLimits') form!: NgForm;
  @Input('headerPos') parentHeader!: HTMLElement;

  readonly tableHeaders = ['Publicist', 'Placering', 'Preroll', 'Midroll', 'Postroll'] as const;
  publishersSig = signal<Publisher[] | null>(null);
  productFormatsSig = signal<ProductFormat[] | null>(null);

  readonly loading = computed(() => this.publishersSig() === null || this.productFormatsSig() === null);
  readonly isEditing = signal(false);
  readonly saving = signal(false);
  readonly estimatesChanged = signal(false);

  readonly selectedPublisher = signal<Publisher | undefined>(undefined);
  readonly selectedProductFormat = signal<ProductFormat | undefined>(undefined);

  readonly displayRows = computed<TableRow[]>(() => {
    const pubs = this.publishersSig();
    const pfs = this.productFormatsSig();
    if (!pubs || !pfs) return [];

    const filteredPFs = pfs.filter(
      (pf) => !this.selectedProductFormat()?.id || pf.id === this.selectedProductFormat()!.id,
    );

    return filteredPFs.map((pf) => ({
      id: pf.id,
      publicist: pubs.find((pub) => pub.id === pf.publisher_id)?.name ?? '',
      placering: pf.name,
      midroll: pf.ag_withdrawal_limits.midroll ?? 0,
      preroll: pf.ag_withdrawal_limits.preroll ?? 0,
      postroll: pf.ag_withdrawal_limits.postroll ?? 0,
      enabled: pf.enabled,
    }));
  });

  private readonly saveRequest = signal<SavePayload | null>(null);

  private publisherService = inject(PublisherService);
  private productFormatService = inject(ProductFormatService);
  private modalService = inject(ModalService);
  private translationService = inject(TranslationService);
  private changeDetection = inject(ChangeDetectorRef);

  constructor() {
    this.translateProductFormat = this.translationService.convertProductFormat;
    this.loadData();

    effect(() => {
      const payload = this.saveRequest();
      if (!payload) return;

      this.saveRequest.set(null);
      void this.performSave(payload);
    });
  }

  private async performSave(payload: SavePayload) {
    const filterActive = !!this.selectedPublisher() || !!this.selectedProductFormat();
    const textBlocks = ['Är du säker på att du vill spara dina ändringar?'];
    if (filterActive) {
      textBlocks.push('Notera att även ändringar i filtrerade rader sparas.');
    }

    this.modalService
      .openConfirm({
        title: 'Bekräfta ändringar',
        texts: textBlocks,
        confirmLabel: 'Spara',
        cancelLabel: 'Avbryt',
      })
      .then(async (response) => {
        if (response.confirm) {
          const loaderModal = this.modalService.openLoader('Sparar ändringar');
          try {
            this.saving.set(true);
            const patched = await this.patchAgWithdrawalLimits(this.productFormatsSig() ?? [], payload);
            const updated = await this.productFormatService.updateProductFormats(patched);
            const patchedIds = patched.map((p) => p.id);
            const updatedData = updated.filter((f) => patchedIds.includes(f.id));
            this.productFormatsSig.set(updatedData);
            this.estimatesChanged.set(true);
          } catch (err) {
            this.modalService.openError(
              'Ajdå, något gick snett. Kunde inte spara ändringar.',
              this.translationService.convertError(err.error.error),
            );
          } finally {
            this.saving.set(false);
            this.changeDetection.markForCheck();
            this.setEditing(false);
            loaderModal.close();
          }
        }
      });
  }

  setEditing(value: boolean): void {
    this.isEditing.set(value);
  }

  cancelEditing(): void {
    this.loadData().then(() => this.isEditing.set(false));
  }

  save(formValue: SavePayload): void {
    if (this.form?.valid) {
      this.saveRequest.set(formValue);
    } else {
      console.warn('Form invalid');
    }
  }

  private async loadData(): Promise<void> {
    try {
      const [publishers, productFormats] = await Promise.all([
        this.publisherService.getPublishers(ExtraPublisherField.enabled),
        this.productFormatService.getProductFormats(
          ExtraProductFormatField.enabled,
          ExtraProductFormatField.agWithdrawalLimits,
          ExtraProductFormatField.slotTypes,
        ),
      ]);
      this.publishersSig.set(publishers.filter((p) => p.enabled));
      this.productFormatsSig.set(productFormats.filter((f) => f.enabled));
    } catch (err) {
      console.error('Failed to load data', err);
    }
  }

  private async confirmSave(filterActive: boolean): Promise<boolean> {
    const textBlocks = ['Är du säker på att du vill spara dina ändringar?'];
    if (filterActive) {
      textBlocks.push('Notera att även ändringar i filtrerade rader sparas.');
    }

    this.modalService
      .openConfirm({
        title: 'Bekräfta ändringar',
        texts: textBlocks,
        confirmLabel: 'Spara',
        cancelLabel: 'Avbryt',
      })
      .then((response) => {
        if (response.confirm) {
          return true;
        }
        return false;
      });

    return false;
  }

  private patchAgWithdrawalLimits(
    productFormats: ProductFormat[],
    formValues: Record<string, number>,
  ): ProductFormat[] {
    return productFormats.map((format) => {
      const updatedLimits: SlotTypeEstimates = { ...format.ag_withdrawal_limits };

      for (const [key, value] of Object.entries(formValues)) {
        const [id, field] = key.split('|');
        if (id === format.id && ['preroll', 'midroll', 'postroll'].includes(field)) {
          updatedLimits[field as SlotType] = value;
        }
      }

      return {
        ...format,
        ag_withdrawal_limits: updatedLimits,
      } as ProductFormat;
    });
  }

  translateProductFormat!: (args: any) => string;
}
